import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { TextInput } from '~/components/Form/Input/TextInput'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import {
  getStoredOrganizations,
  removeStoredOrganization,
  type StoredOrganization
} from '../utils/organizationStorage'
import {
  neworganizationButton,
  organizationList,
  organizationLogo,
  organizationLogoActive,
  organizationLogoButton,
  organizationLogoContainer,
  organizationName,
  organizationSelector
} from './OrganizationSelector.css'

type OrganizationSelectorProps = InputInFormProps & {
  name: string
}

export const OrganizationSelector = ({
  name,
  setValue,
  watch,
  control,
  error,
  register
}: OrganizationSelectorProps): ReactElement | null => {
  const [storedOrganizations, setStoredOrganizations] = useState<
    StoredOrganization[]
  >(getStoredOrganizations())
  const [showOrganizationInput, setShowOrganizationInput] = useState(
    storedOrganizations.length === 0
  )

  if (
    setValue === undefined ||
    name === undefined ||
    watch === undefined ||
    control === undefined ||
    register === undefined
  ) {
    throw new Error('OrganizationSelector must be used inside a Form component')
  }

  useEffect(() => {
    if (storedOrganizations.length > 0) {
      setValue(name, watch(name) ?? storedOrganizations[0]?.slug ?? '')
    }
  }, [storedOrganizations, setValue, name, watch])

  const currentValue = watch(name)
  const organization = storedOrganizations.find((i) => i.slug === currentValue)
  const handleOrganizationSelect = (organization: StoredOrganization): void => {
    setValue(name, organization.slug)
    setShowOrganizationInput(false)
  }

  const handleOrganizationRemove = (
    e: React.MouseEvent,
    slug: string
  ): void => {
    e.stopPropagation()
    removeStoredOrganization(slug)
    const storedOrganizations = getStoredOrganizations()
    setStoredOrganizations(storedOrganizations)
    if (storedOrganizations.length === 0) {
      setValue(name, '')
      setShowOrganizationInput(true)
    } else {
      setValue(name, storedOrganizations[0]?.slug ?? '')
    }
  }

  const handleAddNewOrganization = (): void => {
    setShowOrganizationInput(true)
    setValue(name, '')
  }

  return (
    <AnimatePresence>
      {storedOrganizations.length > 0 && (
        <div className={organizationSelector}>
          <motion.div
            className={organizationList}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
            initial="hidden"
            animate="visible"
          >
            {storedOrganizations.map((organization) => (
              <motion.div
                key={organization.slug}
                variants={{
                  hidden: { opacity: 0, x: 20 },
                  visible: { opacity: 1, x: 0 }
                }}
                initial="hidden"
                animate="visible"
                className={organizationLogoContainer}
                onClick={() => handleOrganizationSelect(organization)}
              >
                <button
                  type="button"
                  className={organizationLogoButton}
                  onClick={(e) =>
                    handleOrganizationRemove(e, organization.slug)
                  }
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
                {organization.logoHeader ? (
                  <img
                    src={organization.logoHeader}
                    alt={organization.name}
                    className={clsx(
                      organizationLogo,
                      organization.slug === currentValue &&
                        organizationLogoActive
                    )}
                  />
                ) : (
                  <div className={organizationLogo}>{organization.name[0]}</div>
                )}
              </motion.div>
            ))}
            <Tooltip text="Autre instance" asChild>
              <Button
                type="button"
                variation={ButtonVariation.Neutral}
                onClick={handleAddNewOrganization}
                narrow
                className={neworganizationButton}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Tooltip>
          </motion.div>
          {organization && (
            <div className={organizationName}>{organization.name}</div>
          )}
        </div>
      )}
      {showOrganizationInput && (
        <TextInput
          key="organization-input"
          label="Instance"
          name={name}
          autoFocus={showOrganizationInput}
          required
          watch={watch}
          setValue={setValue}
          control={control}
          register={register}
          error={error}
        />
      )}
    </AnimatePresence>
  )
}
